import * as React from "react";
import { Link } from "react-router-dom";
import { axiosInstance } from "../../axios/axiosInstance";
import { useNoteAlert } from "../../Context/pleaseNote";
import { instructions, signupImg } from "../../assets/imageImport";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { isAlphaNumericAndSpace_, isAlpha, isNumberOnly, capitalized } from "../../Layout/Common/common";
import { FormikErrorMessage } from "../../Layout/Common/fromikErrorMessage";
import GoogleAddressField, { postcodeAtLocation } from "../../Layout/Common/googleAddressField";
import { FormikSelect } from "../../Layout/Common/formikSelect";
import { GlobalContext } from "../../Context/GlobalContext";
import { useContext } from "react";

const stickyHeaderHeight = () => {
  const header = document.querySelector(".header-area.fixed, .header-area");
  const height = header?.getBoundingClientRect().height ?? 0;
  return getComputedStyle(header ?? document.body).position === "fixed" ? height : 0;
};

const scrollElementIntoView = (element) => {
  const top = window.pageYOffset + element.getBoundingClientRect().top - stickyHeaderHeight() - 24;
  window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
};

const ScrollToError = () => {
  const { errors, submitCount, isValidating } = useFormikContext();
  const focusedSubmitCount = React.useRef(0);

  React.useEffect(() => {
    if (isValidating || submitCount === 0) return;
    if (focusedSubmitCount.current === submitCount) return;

    const firstError = Object.keys(errors)[0];
    if (!firstError) return;

    focusedSubmitCount.current = submitCount;

    const element = document.querySelector(`[name="${firstError}"]`);
    if (!element) return;
    if (element.getClientRects().length === 0) {
      const group = element.closest(".signup-forms") || element.closest(".row") || element.parentElement;
      if (!group) return;
      if (!group.hasAttribute("tabindex")) group.setAttribute("tabindex", "-1");
      group.focus({ preventScroll: true });
      scrollElementIntoView(group);
      return;
    }

    element.focus({ preventScroll: true });
    scrollElementIntoView(element);
  }, [errors, submitCount, isValidating]);

  return null;
};

export const SignupMain = () => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [activeSuggestion, setActiveSuggestion] = React.useState(-1);

  const { showNoteAlert } = useNoteAlert();
  const [signUpDetailsValues, setsignUpDetailsValues] = React.useState(null);
  const [hasFocusedError, setHasFocusedError] = React.useState(false);
  const [showUpdate, setshowUpdate] = React.useState(false);
  const { countryList } = useContext(GlobalContext);
  const countryOptions = React.useMemo(
    () => (countryList ?? []).map((item) => ({ value: capitalized(item.country), label: item.country })),
    [countryList],
  );
  React.useEffect(() => {
    if (showUpdate) {
      window.scrollTo(0, 0);
    }
  }, [showUpdate]);

  const formikRef = React.useRef(null);
  const companyInitialDetailsValues = {
    customerType: "New", // default selection
    wesAccountNumber: "",
    lastInvoiceNumber: "",
    companyName: "",
    abnNumber: "",
    companyWebsite: "",
    firstName: "",
    lastName: "",
    position: "",
    phone: "",
    mobile: "",
    fax: "",
    email: "",
    confirmEmail: "",
    streetAddress: "",
    addressLineTwo: "",
    suburb: "",
    state: "",
    zipcode: "",
    country: "Australia", // default option
    residential: "no", // default selection
    signature: "yes", // default selection
    instruction: "",
    businessType: [],
    businessTypeOther: "",
    categories: [],
    categoriesOther: "",
    terms: false,
  };
  const validationSchema = Yup.object({
    customerType: Yup.string().required("Please select whether you are a New or Existing customer"),

    wesAccountNumber: Yup.string().when("customerType", {
      is: (val) => val === "Existing",
      then: (schema) => schema.required("WES Account Number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    lastInvoiceNumber: Yup.string().when("customerType", {
      is: (val) => val === "Existing",
      then: (schema) => schema.required("Last Purchase Invoice Number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    companyName: Yup.string()
      .required("Company name is required")
      .matches(/^[a-zA-Z0-9 &-]+$/, "Only letters, numbers, spaces"), //.matches(/^[a-zA-Z0-9 &-]+$/, "Only letters, numbers, spaces, & and - are allowed"),

    abnNumber: Yup.string(),

    companyWebsite: Yup.string()
      .matches(/^([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/, "Enter a valid website URL")
      .when("abnNumber", {
        is: (val) => val && val.trim() !== "", // if abnNumber is NOT empty
        then: (schema) => schema.required("Company Website is required when ABN/ACN/Company Number is provided"),
        otherwise: (schema) => schema.notRequired(),
      }),
    firstName: Yup.string()
      .required("First name is required")
      .matches(/^[a-zA-Z0-9 ]+$/, "Only letters, numbers, spaces"),
    lastName: Yup.string()
      .required("Last name is required")
      .matches(/^[a-zA-Z0-9 ]+$/, "Only letters, numbers, spaces"),
    position: Yup.string().nullable(), // optional

    phone: Yup.string().required("Phone is required"),

    fax: Yup.string().nullable(), // optional

    email: Yup.string().email("Enter a valid email").required("Email is required"),

    confirmEmail: Yup.string()
      .oneOf([Yup.ref("email"), null], "Emails must match")
      .required("Confirm email is required"),
    streetAddress: Yup.string().required("Street Address is required"),

    addressLineTwo: Yup.string().nullable(), // optional
    suburb: Yup.string()
      .required("Suburb/Town is required")
      .matches(/^[a-zA-Z0-9 -]+$/, "Only letters, numbers, spaces and hyphens"),

    state: Yup.string()
      .required("State/Province is required")
      .matches(/^[a-zA-Z0-9 -]+$/, "Only letters, numbers, spaces and hyphens"),

    zipcode: Yup.string().required("Post/Zip Code is required").matches(/^\d+$/, "Zip Code must be numeric"),

    country: Yup.string().required("Country is required"),

    residential: Yup.string().required("Residential Address selection is required"),

    signature: Yup.string()
      //.oneOf(["Yes", "No"], "Please select Signature Required")
      .required("Signature Required selection is required"),

    instruction: Yup.string()
      .when("signature", {
        is: (val) => val && val.trim() !== "yes", // if signature is no
        then: (schema) => schema.required("Shipping Instructions are required"),
        otherwise: (schema) => schema.notRequired(),
      })
      .matches(/^[a-zA-Z0-9 ]+$/, "Only letters, numbers, spaces"),

    businessType: Yup.array()
      .min(1, "Please select at least one Business Type")
      .required("Business Type selection is required"),

    businessTypeOther: Yup.string().when("businessType", {
      is: (val) => Array.isArray(val) && val.includes("Other"),
      then: (schema) => schema.required("Please specify your Business Type"),
      otherwise: (schema) => schema.notRequired(),
    }),

    categories: Yup.array()
      .min(1, "Please select at least one Category of Interest")
      .required("Category selection is required"),

    categoriesOther: Yup.string().when("categories", {
      is: (val) => Array.isArray(val) && val.includes("Other"),
      then: (schema) => schema.required("Please specify your Category of Interest"),
      otherwise: (schema) => schema.notRequired(),
    }),
    terms: Yup.boolean()
      .oneOf([true], "You must accept the Terms and Conditions")
      .required("You must accept the Terms and Conditions"),
  });
  const suggestions = [
    "Leave at Front door",
    "Leave at Back door",
    "Leave at Carport",
    "Leave on front Verandah",
    "Sheltered at front of House",
    "Sheltered at Rear of House",
    "Leave with Reception",
    "Leave with Warehouse Attendent",
  ];
  const handleSubmit = (values) => {
    setIsProcessing(true);

    setsignUpDetailsValues(values);
    var data = {
      rdata: values,
    };

    axiosInstance
      .post("Signup/SignUpSubmitClick", values)
      .then((response) => {
        if (response.data != "") {
          if (response.data == "success") {
            document.getElementById("divShowDetails").style.display = "none";

            setIsProcessing(false);
            setshowUpdate(!showUpdate);
          } else {
            showNoteAlert(response.data);
            setIsProcessing(false);
          }
        }
      })
      .catch((error) => {
        setIsProcessing(false);
      });
  };
  const handleOnChangeField = (e, key, setFieldValue) => {
    setFieldValue(key, e.target.value);
  };
  const onSubmitClick = (Validatevalues, { errorsList }, values) => {
    var data = {
      rdata: signUpDetailsValues,
    };
    if (Validatevalues === true) {
    } else {
      setHasFocusedError(false);
    }
  };
  const inputRefs = {
    customerType: React.useRef(null),
    wesAccountNumber: React.useRef(null),
    lastInvoiceNumber: React.useRef(null),
    companyName: React.useRef(null),
    abnNumber: React.useRef(null),
    companyWebsite: React.useRef(null),
    firstName: React.useRef(null),
    lastName: React.useRef(null),
    position: React.useRef(null),
    phone: React.useRef(null),
    mobile: React.useRef(null),
    fax: React.useRef(null),
    email: React.useRef(null),
    confirmEmail: React.useRef(null),
    streetAddress: React.useRef(null),
    addressLineTwo: React.useRef(null),
    suburb: React.useRef(null),
    state: React.useRef(null),
    zipcode: React.useRef(null),
    country: React.useRef(null),
    residential: React.useRef(null),
    signature: React.useRef(null),
    instruction: React.useRef(null),
    businessType: React.useRef(null),
    businessTypeOther: React.useRef(null),
    categories: React.useRef(null),
    categoriesOther: React.useRef(null),
    terms: React.useRef(null),
  };
  // Suburb and State are validated against [a-zA-Z0-9 -], so Google's values have to be folded
  // to ASCII first. Decomposing to NFD and dropping the combining marks turns "Füssen" into
  // "Fussen" instead of "Fssen" - stripping straight to [a-zA-Z0-9 ] would delete the accented
  // letter outright. This is the latinise() step the old ASP.NET page did with a jQuery plugin.
  const sanitizeAlnumSpace = (v) =>
    (v ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9 -]/g, "")
      .trim();
  const fillSuburbFromGoogle = async (autocomplete) => {
    const formik = formikRef.current;
    if (!formik) return;
    const { setFieldValue, setFieldTouched } = formik;
    const place = autocomplete?.getPlace?.();
    // Typing free text and pressing Enter returns a place with a name but no components - there
    // is nothing to trust in that case, so leave what the user typed alone.
    const comps = place?.address_components;
    if (!comps) return;

    const get = (type) => comps.find((c) => c.types.includes(type));
    const locality = get("locality") || get("sublocality") || get("postal_town") || get("administrative_area_level_2");
    const state = get("administrative_area_level_1");

    setFieldValue("suburb", locality ? sanitizeAlnumSpace(locality.long_name) : "");
    setFieldValue("state", state ? sanitizeAlnumSpace(state.short_name) : "");

    let zip = get("postal_code")?.long_name ?? "";
    if (!zip) zip = await postcodeAtLocation(place.geometry?.location);
    setFieldValue("zipcode", /^[a-zA-Z0-9 -]+$/.test(zip) ? zip : "");

    ["suburb", "state", "zipcode"].forEach((f) => setFieldTouched(f, true, false));
  };
  const applyGoogleAddress = async (parsed, place, previousCountry) => {
    const formik = formikRef.current;
    if (!formik) return;
    const { setFieldValue } = formik;
    const components = place?.addressComponents ?? [];
    const getComponent = (...types) =>
      components.find((component) => types.some((type) => component.types.includes(type)));

    const streetAddress = [getComponent("street_number")?.longText, getComponent("route")?.longText]
      .filter(Boolean)
      .join(" ");
    const suburb = getComponent("locality", "sublocality", "postal_town", "administrative_area_level_2");

    setFieldValue("streetAddress", streetAddress || parsed.address1);
    setFieldValue("addressLineTwo", getComponent("subpremise")?.longText ?? "");
    setFieldValue("suburb", sanitizeAlnumSpace(suburb?.longText));
    setFieldValue("state", sanitizeAlnumSpace(parsed.state));
    const googleCountry = countryOptions.find(
      (option) => option.label.toLowerCase() === parsed.country.toLowerCase(),
    );
    setFieldValue("country", googleCountry ? googleCountry.value : previousCountry);

    let postcodeValue = parsed.postCode;
    if (!postcodeValue && place?.location) {
      postcodeValue = await postcodeAtLocation(place.location);
    }
    setFieldValue("zipcode", postcodeValue);
  };
  return (
    <div className="loginWrapper-verification">
      <div className="container-fluid container-lg" id="divShowDetails" style={{ display: "block" }}>
        <div className="row">
          <div className="col-md-7">
            <div className="signupCover">
              <div className="signup-header">
                <h1>Create Your Trade Account</h1>
                <p>
                  Please complete all required fields and submit the form for processing. Our Sales Team will review and
                  verify your information. Once your application has been approved, a confirmation email containing
                  further details will be sent to you.
                </p>
              </div>
              <div className="signup-form">
                <Formik
                  innerRef={formikRef}
                  initialValues={companyInitialDetailsValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, isValid, errors, touched, submitCount, resetForm }) => {
                    return (
                      <Form>
                        <ScrollToError />
                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms">
                              <div className="row">
                                <div className="col-lg-12 signup-innerTitle">
                                  <h2>Customer Type</h2>
                                </div>

                                {/* New or Existing Customer */}
                                <div className="col-12 col-md-12 col-lg-12">
                                  <div className="formCheck-selection mb-2 mb-lg-3">
                                    <label>
                                      Are you New or Existing Customer?
                                      <span className="gl-form-asterisk">*</span>
                                    </label>
                                    <div className="formCheck-selection-list">
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="customerType"
                                          value="New"
                                          id="customerTypeNew"
                                          className="form-check-input"
                                          innerRef={inputRefs.customerType}
                                          onChange={() => {
                                            setFieldValue("customerType", "New");
                                            setFieldValue("wesAccountNumber", "");
                                            setFieldValue("lastInvoiceNumber", "");
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor="customerTypeNew">
                                          New
                                        </label>
                                      </div>
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="customerType"
                                          value="Existing"
                                          id="customerTypeExisting"
                                          className="form-check-input"
                                        />
                                        <label className="form-check-label" htmlFor="customerTypeExisting">
                                          Existing
                                        </label>
                                      </div>
                                    </div>

                                    <ErrorMessage name="customerType">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms">
                              <div className="row">
                                <div className="col-lg-12 signup-innerTitle">
                                  <h2>Company Details</h2>
                                </div>

                                {values.customerType === "Existing" && (
                                  <>
                                    {/* WES Account Number */}
                                    <div className="col-12 col-md-6 col-lg-6 pr-md-5">
                                      <div className="floating-input mb-2 mb-lg-3">
                                        <Field
                                          id="wesAccountNumber"
                                          name="wesAccountNumber"
                                          className={`form-control form-input ${
                                            touched.wesAccountNumber && errors.wesAccountNumber ? "error-message" : ""
                                          }`}
                                          type="text"
                                          placeholder=""
                                          maxLength="20"
                                          innerRef={inputRefs.wesAccountNumber}
                                        />
                                        <label htmlFor="wesAccountNumber">
                                          WES Account Number
                                          <span className="gl-form-asterisk">*</span>
                                        </label>

                                        <ErrorMessage name="wesAccountNumber">
                                          {(msg) => <FormikErrorMessage message={msg} />}
                                        </ErrorMessage>
                                      </div>
                                    </div>

                                    {/* Last Purchase Invoice Number */}
                                    <div className="col-12 col-md-6 col-lg-6 pl-md-5">
                                      <div className="floating-input mb-2 mb-lg-3">
                                        <Field
                                          id="lastInvoiceNumber"
                                          name="lastInvoiceNumber"
                                          className={`form-control form-input ${
                                            touched.lastInvoiceNumber && errors.lastInvoiceNumber ? "error-message" : ""
                                          }`}
                                          type="text"
                                          placeholder=""
                                          maxLength="20"
                                          innerRef={inputRefs.lastInvoiceNumber}
                                        />
                                        <label htmlFor="lastInvoiceNumber">
                                          Last Purchase Invoice Number
                                          <span className="gl-form-asterisk">*</span>
                                        </label>

                                        <ErrorMessage name="lastInvoiceNumber">
                                          {(msg) => <FormikErrorMessage message={msg} />}
                                        </ErrorMessage>
                                      </div>
                                    </div>
                                  </>
                                )}
                                {/* Company Name */}
                                <div className="col-12 col-md-6 col-lg-6 pr-md-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="companyName"
                                      name="companyName"
                                      className={`form-control form-input ${
                                        touched.companyName && errors.companyName ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) =>
                                        setFieldValue("companyName", isAlphaNumericAndSpace_(e.target.value))
                                      }
                                      innerRef={inputRefs.companyName}
                                    />
                                    <label htmlFor="companyName">
                                      Company/Receiver Name
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="companyName">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* ABN */}
                                <div className="col-12 col-md-6 col-lg-6 pl-md-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="abnNumber"
                                      name="abnNumber"
                                      className={`form-control form-input ${
                                        touched.abnNumber && errors.abnNumber ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      maxLength="20"
                                      innerRef={inputRefs.abnNumber}
                                    />
                                    <label htmlFor="abnNumber">
                                      ABN / ACN / Company Number
                                      {/* <span className="gl-form-asterisk">*</span>*/}
                                    </label>

                                    <ErrorMessage name="abnNumber">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Website */}
                                <div className="col-12 col-md-12 col-lg-12">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="companyWebsite"
                                      name="companyWebsite"
                                      className={`form-control form-input ${
                                        touched.companyWebsite && errors.companyWebsite ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      innerRef={inputRefs.companyWebsite}
                                    />
                                    <label htmlFor="companyWebsite">
                                      Company Website
                                      {/* <span className="gl-form-asterisk">*</span>*/}
                                    </label>

                                    <ErrorMessage name="companyWebsite">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms">
                              <div className="row">
                                <div className="col-lg-12 signup-innerTitle">
                                  <h2>Contact Details</h2>
                                </div>

                                {/* First Name */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="firstName"
                                      name="firstName"
                                      className={`form-control form-input ${
                                        touched.firstName && errors.firstName ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      maxLength="30"
                                      innerRef={inputRefs.firstName}
                                      onChange={(e) => setFieldValue("firstName", isAlpha(e.target.value))}
                                    />
                                    <label htmlFor="firstName">
                                      First Name
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="firstName">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Last Name */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="lastName"
                                      name="lastName"
                                      className={`form-control form-input ${
                                        touched.lastName && errors.lastName ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      maxLength="30"
                                      innerRef={inputRefs.lastName}
                                      onChange={(e) => setFieldValue("lastName", isAlpha(e.target.value))}
                                    />
                                    <label htmlFor="lastName">
                                      Last Name
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="lastName">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Position */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="position"
                                      name="position"
                                      className="form-control form-input"
                                      type="text"
                                      placeholder=""
                                      innerRef={inputRefs.position}
                                      maxLength="30"
                                    />
                                    <label htmlFor="position">Position</label>
                                  </div>
                                </div>

                                {/* Phone */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="phone"
                                      name="phone"
                                      className={`form-control form-input ${
                                        touched.phone && errors.phone ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) => setFieldValue("phone", isNumberOnly(e.target.value))}
                                      innerRef={inputRefs.phone}
                                      maxLength="30"
                                    />
                                    <label htmlFor="phone">
                                      Phone
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="phone">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Mobile */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="mobile"
                                      name="mobile"
                                      className={`form-control form-input ${
                                        touched.mobile && errors.mobile ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) => setFieldValue("mobile", isNumberOnly(e.target.value))}
                                      innerRef={inputRefs.mobile}
                                      maxLength="30"
                                    />
                                    <label htmlFor="mobile">Mobile/Cell Phone</label>

                                    <ErrorMessage name="mobile">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Fax */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      id="fax"
                                      name="fax"
                                      className="form-control form-input"
                                      type="text"
                                      placeholder=""
                                      innerRef={inputRefs.fax}
                                      maxLength="30"
                                    />
                                    <label htmlFor="fax">Fax</label>
                                  </div>
                                </div>

                                {/* Email */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input floatingEmail-input mb-2 mb-lg-3">
                                    <Field
                                      id="email"
                                      name="email"
                                      className={`form-control form-input ${
                                        touched.email && errors.email ? "error-message" : ""
                                      }`}
                                      type="email"
                                      innerRef={inputRefs.email}
                                    />
                                    <label htmlFor="email">
                                      Email address
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="email">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Confirm Email */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input floatingEmail-input mb-2 mb-lg-3">
                                    <Field
                                      id="confirmEmail"
                                      name="confirmEmail"
                                      className={`form-control form-input ${
                                        touched.confirmEmail && errors.confirmEmail ? "error-message" : ""
                                      }`}
                                      type="email"
                                      innerRef={inputRefs.confirmEmail}
                                    />
                                    <label htmlFor="confirmEmail">
                                      Confirm Email
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="confirmEmail">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms">
                              <div className="row">
                                <div className="col-lg-12 signup-innerTitle">
                                  <h2>Address Details</h2>
                                </div>

                                {/* Street Address */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <GoogleAddressField
                                      formik={formikRef.current}
                                      setFieldValue={setFieldValue}
                                      formValue={values}
                                      name="streetAddress"
                                      label={
                                        <>
                                          Street Address
                                          <span className="gl-form-asterisk">*</span>
                                        </>
                                      }
                                      errors={errors.streetAddress}
                                      touched={touched.streetAddress}
                                      innerRef={inputRefs.streetAddress}
                                      maxLength="150"
                                      fields={{ postCode: "zipcode" }}
                                      onAddressSelected={(parsed, _setFieldValue, place) =>
                                        applyGoogleAddress(parsed, place, values.country)
                                      }
                                    />

                                    <ErrorMessage name="streetAddress">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Address Line 2 */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      name="addressLineTwo"
                                      id="txtAddresslinetwo"
                                      className={`form-control form-input ${
                                        touched.addressLineTwo && errors.addressLineTwo ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      innerRef={inputRefs.addressLineTwo}
                                      maxLength="150"
                                    />
                                    <label htmlFor="addresslinetwo">Address Line 2</label>

                                    <ErrorMessage name="addressLineTwo">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Suburb */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      name="suburb"
                                      id="suburb"
                                      className={`form-control form-input ${
                                        touched.suburb && errors.suburb ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) => setFieldValue("suburb", isAlphaNumericAndSpace_(e.target.value))}
                                      innerRef={inputRefs.suburb}
                                      maxLength="100"
                                    />
                                    <label htmlFor="suburb">
                                      Suburb/Town
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="suburb">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* State */}
                                <div className="col-6 pl-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      name="state"
                                      id="txtstate"
                                      className={`form-control form-input ${
                                        touched.state && errors.state ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) => setFieldValue("state", isAlphaNumericAndSpace_(e.target.value))}
                                      innerRef={inputRefs.state}
                                      maxLength="100"
                                    />
                                    <label htmlFor="state">
                                      State/Province
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="state">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Zip Code */}
                                <div className="col-6 pr-5">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      name="zipcode"
                                      id="txtzipcode"
                                      className={`form-control form-input ${
                                        touched.zipcode && errors.zipcode ? "error-message" : ""
                                      }`}
                                      type="text"
                                      placeholder=""
                                      onChange={(e) => setFieldValue("zipcode", isNumberOnly(e.target.value))}
                                      innerRef={inputRefs.zipcode}
                                      maxLength="50"
                                    />
                                    <label htmlFor="zipcode">
                                      Post/Zip Code
                                      <span className="gl-form-asterisk">*</span>
                                    </label>

                                    <ErrorMessage name="zipcode">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Country */}
                                <div className="col-6 pl-5">
                                  <div className="choose-state mb-2 mb-lg-3">
                                    <label htmlFor="country">
                                      Country <span className="gl-form-asterisk">*</span>
                                    </label>
                                    <FormikSelect
                                      name="country"
                                      options={countryOptions}
                                      placeholder="Select Country"
                                      innerRef={inputRefs.country}
                                    />

                                    <ErrorMessage name="country">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Residential Address */}
                                <div className="col-6 pr-5">
                                  <div className="formCheck-selection mb-2 mb-lg-3">
                                    <label>
                                      Is this a Residential Address <span className="gl-form-asterisk">*</span>
                                    </label>
                                    <div className="formCheck-selection-list">
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="residential"
                                          value="yes"
                                          id="residentialOne"
                                          className="form-check-input"
                                          innerRef={inputRefs.residential}
                                        />
                                        <label className="form-check-label" htmlFor="residentialOne">
                                          Yes
                                        </label>
                                      </div>
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="residential"
                                          value="no"
                                          id="residentialTwo"
                                          className="form-check-input"
                                          innerRef={inputRefs.residential}
                                        />
                                        <label className="form-check-label" htmlFor="residentialTwo">
                                          No
                                        </label>
                                      </div>
                                    </div>

                                    <ErrorMessage name="residential">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Signature Required */}
                                <div className="col-6 pl-5">
                                  <div className="formCheck-selection mb-2 mb-lg-3">
                                    <label>
                                      Signature Required On Delivery <span className="gl-form-asterisk">*</span>
                                    </label>
                                    <div className="formCheck-selection-list">
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="signature"
                                          value="yes"
                                          id="signatureOne"
                                          className="form-check-input"
                                          innerRef={inputRefs.signature}
                                        />
                                        <label className="form-check-label" htmlFor="signatureOne">
                                          Yes
                                        </label>
                                      </div>
                                      <div className="form-check custom-check">
                                        <Field
                                          type="radio"
                                          name="signature"
                                          value="no"
                                          id="signatureTwo"
                                          className="form-check-input"
                                          innerRef={inputRefs.signature}
                                        />
                                        <label className="form-check-label" htmlFor="signatureTwo">
                                          No
                                        </label>
                                      </div>
                                    </div>

                                    <ErrorMessage name="signature">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>

                                {/* Shipping Instructions */}
                                <div className="col-12">
                                  <div className="floating-input mb-2 mb-lg-3">
                                    <Field
                                      name="instruction"
                                      id="instruction"
                                      className={`form-control form-input ${
                                        touched.instruction && errors.instruction ? "error-message" : ""
                                      }`}
                                      type="text"
                                      onFocus={() => {
                                        setActiveSuggestion(-1);
                                        setShowSuggestions(true);
                                      }}
                                      onChange={(e) =>
                                        setFieldValue("instruction", isAlphaNumericAndSpace_(e.target.value))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                          e.preventDefault();
                                          const step = e.key === "ArrowDown" ? 1 : -1;
                                          if (!showSuggestions) {
                                            setShowSuggestions(true);
                                            setActiveSuggestion(step === 1 ? 0 : suggestions.length - 1);
                                            return;
                                          }
                                          setActiveSuggestion((current) =>
                                            current === -1
                                              ? step === 1
                                                ? 0
                                                : suggestions.length - 1
                                              : (current + step + suggestions.length) % suggestions.length,
                                          );
                                        } else if (e.key === "Enter" && showSuggestions && activeSuggestion >= 0) {
                                          e.preventDefault();
                                          setFieldValue("instruction", suggestions[activeSuggestion]);
                                          setShowSuggestions(false);
                                          setActiveSuggestion(-1);
                                        } else if (e.key === "Escape" && showSuggestions) {
                                          e.preventDefault();
                                          setShowSuggestions(false);
                                          setActiveSuggestion(-1);
                                        }
                                      }}
                                      onBlur={() => {
                                        setTimeout(() => {
                                          setShowSuggestions(false);
                                          setActiveSuggestion(-1);
                                        }, 200);
                                      }}
                                      innerRef={inputRefs.instruction}
                                      maxLength="50"
                                    />
                                    {/* Suggestions */}
                                    {showSuggestions && (
                                      <div
                                        className="dliv_Suggestion dropdown-menu show w-100 shadow-sm mt-1"
                                        style={{ top: "100%", left: 0 }}
                                      >
                                        {suggestions.map((sug, idx) => (
                                          <div
                                            key={idx}
                                            className="sug dropdown-item"
                                            style={{
                                              cursor: "pointer",
                                              ...(idx === activeSuggestion && {
                                                backgroundColor: "var(--bs-dropdown-link-hover-bg)",
                                                color: "var(--bs-dropdown-link-hover-color)",
                                              }),
                                            }}
                                            onMouseEnter={() => setActiveSuggestion(idx)}
                                            onClick={() => setFieldValue("instruction", sug)}
                                          >
                                            {sug}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <label htmlFor="instruction">
                                      Shipping Instructions
                                      {/* <span className="gl-form-asterisk">*</span>*/}
                                    </label>

                                    <ErrorMessage name="instruction">
                                      {(msg) => <FormikErrorMessage message={msg} />}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms businesstype-form">
                              <div className="row">
                                <div className="col-lg-12 signup-innerTitle business-innerTitle">
                                  <h2>
                                    Business Type <span>*</span>
                                  </h2>
                                  <p className="mt-1">Please make a selection from the options below.</p>
                                </div>

                                {/* Contractor */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Contractor"
                                      id="contractor"
                                      className="form-check-input"
                                      innerRef={inputRefs.businessType}
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="contractor">
                                      Contractor
                                    </label>
                                  </div>
                                </div>

                                {/* SME */}
                                <div className="col-6 col-lg-4 pl-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="SME"
                                      id="sme"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="sme">
                                      SME
                                    </label>
                                  </div>
                                </div>

                                {/* Corporate */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Corporate"
                                      id="corporate"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="corporate">
                                      Corporate
                                    </label>
                                  </div>
                                </div>
                                {/* Government  */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Government"
                                      id="government"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="government">
                                      Government
                                    </label>
                                  </div>
                                </div>
                                {/* Education  */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Education"
                                      id="education"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="education">
                                      Education
                                    </label>
                                  </div>
                                </div>
                                {/* Retail Store */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Retail Store"
                                      id="retailStore"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="retailStore">
                                      Retail Store
                                    </label>
                                  </div>
                                </div>
                                {/* Online Store */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Online Store"
                                      id="onlineStore"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="onlineStore">
                                      Online Store
                                    </label>
                                  </div>
                                </div>
                                {/* Reseller / Wholesaler  */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Reseller / Wholesaler "
                                      id="resellerwholesaler"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="resellerwholesaler">
                                      Reseller / Wholesaler
                                    </label>
                                  </div>
                                </div>
                                {/* Home User / Hobbyist */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Home User / Hobbyist"
                                      id="homeUserHobbyist"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="homeUserHobbyist">
                                      Home User / Hobbyist
                                    </label>
                                  </div>
                                </div>
                                {/* Other */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="businessType"
                                      value="Other"
                                      id="businessTypeOtherOption"
                                      className="form-check-input"
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const selected = Array.isArray(values.businessType) ? values.businessType : [];
                                        setFieldValue(
                                          "businessType",
                                          isChecked ? [...selected, "Other"] : selected.filter((v) => v !== "Other"),
                                        );
                                        if (!isChecked) {
                                          setFieldValue("businessTypeOther", "");
                                        }
                                      }}
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="businessTypeOtherOption"
                                    >
                                      Other
                                    </label>
                                  </div>
                                </div>

                                {/* Other - free text */}
                                {Array.isArray(values.businessType) && values.businessType.includes("Other") && (
                                  <div className="col-12 col-lg-12">
                                    <div className="floating-input mb-2 mb-lg-3">
                                      <Field
                                        id="businessTypeOther"
                                        name="businessTypeOther"
                                        className={`form-control form-input ${
                                          touched.businessTypeOther && errors.businessTypeOther ? "error-message" : ""
                                        }`}
                                        type="text"
                                        placeholder=""
                                        maxLength="100"
                                        innerRef={inputRefs.businessTypeOther}
                                      />
                                      <label htmlFor="businessTypeOther">
                                        Please specify your Business Type
                                        <span className="gl-form-asterisk">*</span>
                                      </label>

                                      <ErrorMessage name="businessTypeOther">
                                        {(msg) => <FormikErrorMessage message={msg} />}
                                      </ErrorMessage>
                                    </div>
                                  </div>
                                )}

                                {/* Error message */}
                                <div className="col-12 col-lg-12">
                                  <ErrorMessage name="businessType">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-12">
                            <div className="signup-forms category-interest">
                              <div className="row">
                                <div className="col-12 col-lg-12 signup-innerTitle business-innerTitle">
                                  <h2>
                                    Categories of Interest <span>*</span>
                                  </h2>
                                  <p className="mt-1">Please make a selection from the options below.</p>
                                </div>

                                {/* Audio/Visual */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Audio/Visual"
                                      id="businessaudio"
                                      className="form-check-input"
                                      innerRef={inputRefs.categories}
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="businessaudio">
                                      Audio/Visual
                                    </label>
                                  </div>
                                </div>

                                {/* Data & Networking */}
                                <div className="col-6 col-lg-4 pl-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Data & Networking"
                                      id="businessdata"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="businessdata">
                                      Data & Networking
                                    </label>
                                  </div>
                                </div>

                                {/* Audio */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Audio"
                                      id="categoryaudio"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="categoryaudio">
                                      Audio
                                    </label>
                                  </div>
                                </div>
                                {/* Security */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Security"
                                      id="categorySecurity"
                                      className="form-check-input"
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="categorySecurity">
                                      Security
                                    </label>
                                  </div>
                                </div>
                                {/* Communications */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Communications"
                                      id="categoryCommunications"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryCommunications"
                                    >
                                      Communications
                                    </label>
                                  </div>
                                </div>
                                {/* Power & Lighting */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Power & Lighting"
                                      id="categoryPowerLighting"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryPowerLighting"
                                    >
                                      Power & Lighting
                                    </label>
                                  </div>
                                </div>
                                {/* Electronic Components */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Electronic Components"
                                      id="categoryElectronicComponents"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryElectronicComponents"
                                    >
                                      Electronic Components
                                    </label>
                                  </div>
                                </div>
                                {/* Tools and Test Equipment */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Tools and Test Equipment"
                                      id="categoryToolsEquipment"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryToolsEquipment"
                                    >
                                      Tools and Test Equipment
                                    </label>
                                  </div>
                                </div>
                                {/* Automotive Acc. */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Automotive Acc."
                                      id="categoryAutomotiveAcc"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryAutomotiveAcc"
                                    >
                                      Automotive Acc.
                                    </label>
                                  </div>
                                </div>
                                {/* All Categories */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="All Categories"
                                      id="categoryAllCategories"
                                      className="form-check-input"
                                    />
                                    <label
                                      className="form-check-label w-100 text-center"
                                      htmlFor="categoryAllCategories"
                                    >
                                      All Categories
                                    </label>
                                  </div>
                                </div>

                                {/* Other */}
                                <div className="col-6 col-lg-4 pr-5">
                                  <div className="form-check signupForm-radio mb-2 mb-lg-3">
                                    <Field
                                      type="checkbox"
                                      name="categories"
                                      value="Other"
                                      id="categoryOtherOption"
                                      className="form-check-input"
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const selected = Array.isArray(values.categories) ? values.categories : [];
                                        setFieldValue(
                                          "categories",
                                          isChecked ? [...selected, "Other"] : selected.filter((v) => v !== "Other"),
                                        );
                                        if (!isChecked) {
                                          setFieldValue("categoriesOther", "");
                                        }
                                      }}
                                    />
                                    <label className="form-check-label w-100 text-center" htmlFor="categoryOtherOption">
                                      Other
                                    </label>
                                  </div>
                                </div>

                                {/* Other - free text */}
                                {Array.isArray(values.categories) && values.categories.includes("Other") && (
                                  <div className="col-12 col-lg-12">
                                    <div className="floating-input mb-2 mb-lg-3">
                                      <Field
                                        id="categoriesOther"
                                        name="categoriesOther"
                                        className={`form-control form-input ${
                                          touched.categoriesOther && errors.categoriesOther ? "error-message" : ""
                                        }`}
                                        type="text"
                                        placeholder=""
                                        maxLength="100"
                                        innerRef={inputRefs.categoriesOther}
                                      />
                                      <label htmlFor="categoriesOther">
                                        Please specify your Category of Interest
                                        <span className="gl-form-asterisk">*</span>
                                      </label>

                                      <ErrorMessage name="categoriesOther">
                                        {(msg) => <FormikErrorMessage message={msg} />}
                                      </ErrorMessage>
                                    </div>
                                  </div>
                                )}

                                {/* Validation error */}
                                <div className="col-12 col-lg-12">
                                  <ErrorMessage name="categories">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-12 col-lg-12">
                            <div className="signup-forms category-interest">
                              <div className="form-check custom-check signupConfirm">
                                <div className="signature-req-input">
                                  <Field
                                    type="checkbox"
                                    name="terms"
                                    id="signupConfrimation"
                                    className="form-check-input"
                                  />
                                </div>
                                <label className="form-check-label" htmlFor="signupConfrimation">
                                  I have read and understand Sales{" "}
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open("/termsandconditions", "_blank", "noopener,noreferrer");
                                    }}
                                    className="text-decoration-underline"
                                    target="_blank"
                                  >
                                    Terms and Conditions
                                  </a>
                                </label>
                              </div>

                              <ErrorMessage name="terms">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                            </div>
                          </div>
                        </div>

                        <div className="row mt-4">
                          <div className="col-12 col-lg-12">
                            <div className="signupbtns">
                              <div className="button-effect">
                                <button
                                  type="submit"
                                  className="btn btn-products theme-primary"
                                  disabled={isProcessing}
                                  onClick={() => onSubmitClick(isValid, errors, setFieldValue)}
                                >
                                  {isProcessing ? "Processing..." : "Create Account"}
                                </button>
                              </div>
                              <a
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  resetForm();
                                  window.scrollTo(0, 0);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    resetForm();
                                    window.scrollTo(0, 0);
                                  }
                                }}
                              >
                                Reset
                              </a>
                            </div>
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
          <div className="col-md-5 mt-4 mt-lg-0">
            <div className="signup-instrcutions">
              <div className="signup-instrcutions-img">
                <figure>
                  <img src={signupImg} className="img-fluid w-100" alt="Signup Image" />
                </figure>
              </div>
              <div className="signup-instrcutions-content p-4">
                <h3>Solutions for Trade Professionals</h3>
                <p>
                  With over 40 years of electronics industry expertise, WES is a trusted supplier of Audio,
                  Audio/Visual, Data, Electronic Components, and Electrical products. We provide trade customers with
                  competitive pricing, rapid delivery, and seamless 24/7 online ordering.
                </p>
                <ul className="signup-instrcutions-list">
                  <li className="d-flex align-items-start">
                    <img src={instructions} alt="instruction-icon" />
                    <span>
                      <b>Access to Premium Brands:</b> Shop top-tier Audio, AV, Data, and Electrical products from
                      leading manufactures.
                    </span>
                  </li>
                  <li className="d-flex align-items-start">
                    <img src={instructions} alt="instruction-icon" />
                    <span>
                      <b>Fast Nationwide Delivery: </b> Enjoy fast, reliable shipping across Australia, same-day
                      dispatch to keep your projects on schedule.
                    </span>
                  </li>
                  <li className="d-flex align-items-start">
                    <img src={instructions} alt="instruction-icon" />
                    <span>
                      <b>Trade Pick-Up Counter:</b> Collect orders at your convenience from dedicated trade counter in
                      Sydney.
                    </span>
                  </li>
                  <li className="d-flex align-items-start">
                    <img src={instructions} alt="instruction-icon" />
                    <span>
                      <b>24/7 Online Ordering Platform:</b>Order, track, and manage your account anytime online. Add and
                      manage multiple company user logins for seamless team access.
                    </span>
                  </li>
                  <li className="d-flex align-items-start">
                    <img src={instructions} alt="instruction-icon" />
                    <span>
                      <b>Exclusive Trade Pricing and Promotions:</b>Unlock trade pricing and promotions for bulk
                      savings, with updates via our newsletter and online platform.{" "}
                    </span>
                  </li>
                </ul>
                <div className="signup-instrcutions-help mt-4">
                  <h3>Need Help?</h3>
                  <p>Need help registering your account or have questions? We’d be delighted to assist you.</p>
                  <span>02 9797 9866</span>
                  <a href="mailto:sales@wes.com.au" className="text-decoration-underline">
                    sales@wes.com.au
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="container-fluid container-lg"
        id="divUpdateSuccess"
        style={{ display: showUpdate ? "block" : "none" }}
      >
        <div className="row">
          <div className="col-md-8 offset-md-2">
            <div className="signup-successful text-center">
              <div className="signup-successful-inner">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M30 55C43.75 55 55 43.75 55 30C55 16.25 43.75 5 30 5C16.25 5 5 16.25 5 30C5 43.75 16.25 55 30 55Z"
                    stroke="#3BA429"
                    strokeWidth="3.375"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M19.375 29.9998L26.45 37.0748L40.625 22.9248"
                    stroke="#3BA429"
                    strokeWidth="3.375"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
                <h1 className="my-4">Thanks for Registering!</h1>
                <p className="mt-1">We've received your details and are processing your registration.</p>
                <p className="mt-1">Expect to hear from us by email or phone within the next 1–2 business days.</p>
                <div className="button-effect mt-5 d-flex align-items-center justify-content-center">
                  <Link to="/Login" className="theme-primary">
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
